
import React from 'react'
import '../styles/_financial_info.scss';
import { useTranslation } from 'react-i18next';
import { useNumberFormatter } from '../../../../../hooks/useNumberFormatter';
import { HeaderPmr } from '../../components/HeaderPmr/HeaderPmr';
import ChartProgressBarReport from '../../../../Portfolio_R/SummaryCharts/components/ChartProgressBarReport';
import PariPassuTable from '../../components/PariPassuTable/PariPassuTable';
import YearDisbursements from '../../components/YearDisbursements/YearDisbursements';
import { CardInfo } from '../../../../Portfolio_R/SummaryCharts/components/CardInfo';
import { ReactComponent as IconCheck } from '../../../../../assets/icons/u_alert-check.svg';
import { ReactComponent as IconAlert } from '../../../../../assets/icons/u_alert-multyple.svg';
import { capitalizeFirstLetter } from '../../../../../services/stringUtil';
import { mapGeneralInfoInDataSource } from '../../utils/Helpers/MapperHelper';
import { formatDate } from '../../utils/Helpers/DataFormatHelper';

export const FinancialInfoSection = ({ data }) => {
    const { t } = useTranslation();
    const { formatNumber } = useNumberFormatter();
    const dataSource = data.sectionTwoFinantialInformation;

    const {
        counterpartLocal,
        advances,
        efas,
        paripassu,
        disbursementAnnual
    } = dataSource;

    return (
        <section className='financial-report'>
            <HeaderPmr data={mapGeneralInfoInDataSource(data)} />
            <article className="financial-info-report">
                <h3 className='content-layout-title'>{t('extended_report.complementary_financial_info')}</h3>
                <section className='financial-info-layout'>
                    <div className='local-counterpart'>
                        <h3 className="chart-title">{capitalizeFirstLetter(t('local_counterpart').toLowerCase())}</h3>
                        <h3 className='chart-subtitle'>{t('amount')}: {formatNumber(counterpartLocal.counterpartLocalAmountTotal)} (USD)</h3>
                        <ChartProgressBarReport
                            completed={counterpartLocal.counterpartLocalPercentDisbursement}
                            totalAmount={counterpartLocal.counterpartLocalAmountTotal}
                            disbursmentAmount={counterpartLocal.counterpartLocalAmountJustified}
                            completedLabel={t('justified')}
                            pendingLabel={t('pending')} />
                    </div>
                    <div className='pari-passu'>
                        <PariPassuTable
                            deviationPercent={paripassu.pariPassuDeviationPercent}
                            deviationAmount={paripassu.pariPassuDeviationAmount}
                            contractualBIDAmount={paripassu.contractualBid}
                            contractualLocalAmount={paripassu.contractualLocal}
                            currentBIDAmount={paripassu.currentBID}
                            currentLocalAmount={paripassu.currentLocal}
                        />
                    </div>
                    <div className='year-disbursements'>
                        <YearDisbursements chart={disbursementAnnual} />
                    </div>
                    <div className='advances'>
                        <h3 className="chart-title">{capitalizeFirstLetter(t('anticipations'))}</h3>
                        <div className='advances-distribution'>
                            <div className='last-advances'>
                                <h3 className='chart-subtitle'>{capitalizeFirstLetter(t('last_anticipation_made'))}</h3>
                                <div className='data-card'>
                                    <CardInfo
                                        title={t('amount')}
                                        data={formatNumber(advances.amount)}
                                        units={t('dolar_sign')} />
                                    <CardInfo
                                        title={t('date')}
                                        data={formatDate(advances.transactionDate)} />
                                    <CardInfo
                                        title={capitalizeFirstLetter(t('plan')) + ' ' + advances.financialPlanDays}
                                        data={formatDate(advances.financialPlanDate)} />
                                </div>
                            </div>
                            <div className='current-justify'>
                                <h3 className='chart-subtitle'>{capitalizeFirstLetter(t('justified_to_date'))} (USD)</h3>
                                <div className='data-card'>
                                    <CardInfo
                                        title={capitalizeFirstLetter(t('last_justify'))}
                                        data={formatNumber(advances.lastJustify)} />
                                    <CardInfo
                                        title={t('date')}
                                        data={formatDate(advances.justifyDate)} />
                                    <CardInfo
                                        title={capitalizeFirstLetter(t('anticipation_remain'))}
                                        data={formatNumber(advances.advanceBalance)} />
                                    <CardInfo
                                        title={t('justify_percentage')}
                                        data={formatNumber(advances.percentToJustify)}
                                        units='%' />
                                    <div className='card-content'>
                                        <CardInfo
                                            title={capitalizeFirstLetter(t('new_disbursement_minimum'))}
                                            data={formatNumber(advances.minToNewDisbursement)} />
                                        <div className="content-title alert">
                                            {advances.checkEnabled === 'enabled' ? (
                                                <><IconCheck /> {t('enabled')}</>
                                            ) : (
                                                <><IconAlert /> {t('no_enabled')}</>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='financial-statements'>
                        <h3 className="chart-title">{t('efas_finance')}</h3>
                        <div className='financial-statements-distribution'>
                            <div>
                                <h3 className='chart-subtitle'>{capitalizeFirstLetter(t('last_presented'))}</h3>
                                <div className='chart-content'>
                                    <CardInfo title={t('efa_presentation_date')} data={efas.lastPresentations?.fiscalyear || '-'} />
                                    <CardInfo title={t('efa_audit_company')} data={efas.lastPresentations?.efaAuditCompany || '-'} />
                                    <CardInfo title={t('expiration')} data={formatDate(efas.lastPresentations?.dueExpiration)} />
                                    <CardInfo title={t('status')} data={efas.lastPresentations?.state ? t(`efas_pending_states_${efas.lastPresentations?.state}`) : '-'} />
                                    <CardInfo title={capitalizeFirstLetter(t('presentation_date'))} data={formatDate(efas.lastPresentations?.presentationDate)} />
                                    <CardInfo title={t('efa_opinion')} data={t(efas.lastPresentations?.efaComment || '-')} />
                                </div>
                            </div>
                            <div>
                                <h3 className='chart-subtitle'>{capitalizeFirstLetter(t('next_to_present'))}</h3>
                                <div className='chart-content'>
                                    <CardInfo title={t('efa_presentation_date')} data={efas.nextPresentations?.fiscalyear || '-'} />
                                    <CardInfo title={t('efa_audit_company')} data={efas.nextPresentations?.efaAuditCompany || '-'} />
                                    <CardInfo title={t('expiration')} data={formatDate(efas.nextPresentations?.dueExpiration)} />
                                    <CardInfo title={t('status')} data={efas.nextPresentations?.state ? t(`efas_pending_states_${efas.nextPresentations?.state}`) : '-'} />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </article>
        </section >
    );
};
